/**
 * "My questions" (#1681) — the signed-in person's own Help Desk reports, and
 * their answer to "Fixed — check it".
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";

export interface MyCase {
    id: string;
    title: string;
    group: "check" | "open" | "done";
    where: string;
    urgent: boolean;
    link: string | null;
    askedAt: string;
}

export const myCasesKey = ["help-desk", "my-cases"] as const;

export function useMyCases(enabled = true) {
    return useQuery<{ cases: MyCase[]; owner: string | null }>({
        queryKey: myCasesKey,
        queryFn: () => api.get(`/api/help-desk/my-cases`),
        enabled,
        staleTime: 30_000,
        retry: false,
    });
}

export function useConfirmCase() {
    const qc = useQueryClient();
    return useMutation<{ ok: boolean; message: string }, Error, { id: string; ok: boolean; note?: string }>({
        mutationFn: async ({ id, ok, note }) => {
            try {
                return await api.post(`/api/help-desk/my-cases/${id}/confirm`, { ok, note: note ?? null });
            } catch (e) {
                if (e instanceof ApiError && e.body && typeof e.body === "object" && "message" in e.body) {
                    throw new Error(String((e.body as { message: unknown }).message));
                }
                throw e;
            }
        },
        onSettled: () => void qc.invalidateQueries({ queryKey: myCasesKey }),
    });
}
